import { Link, useNavigate } from '@tanstack/react-router'
import { Button } from '@workspace/ui/components/button'
import { ErrorBanner } from '@workspace/ui/components/error-banner'
import { LoadingSpinner } from '@workspace/ui/components/loading-spinner'
import {
  AlertTriangle,
  BarChart3,
  CheckCircle2,
  Coins,
  FileText,
  MessageSquare,
  Mic,
  Trophy,
} from 'lucide-react'
import { useState } from 'react'
import { DocumentUpload } from '../../../components/document-upload'
import {
  useCreatePitchMutation,
  useStartupCreditBalanceQuery,
  useStartupQuery,
} from '../../../hooks/api'

function isInsufficientCreditsError(err: unknown): boolean {
  if (typeof err === 'object' && err !== null && 'error' in err) {
    const apiError = err as { error?: { message?: string } }
    return apiError.error?.message === 'Insufficient credits'
  }
  return err instanceof Error && err.message === 'Insufficient credits'
}

function getErrorMessage(err: unknown): string {
  if (typeof err === 'object' && err !== null && 'error' in err) {
    const apiError = err as { error?: { message?: string } }
    if (apiError.error?.message) return apiError.error.message
  }
  if (err instanceof Error) return err.message
  return 'Failed to create pitch session'
}

interface PitchPrepareProps {
  /** Investor ID to link the pitch session to a specific investor */
  investorId?: string
}

export function PitchPrepare({ investorId }: PitchPrepareProps) {
  const navigate = useNavigate()
  const { data: startupResponse, isLoading } = useStartupQuery()
  const startup = startupResponse?.data

  // Credit balance: only check when startup pays (no investorId).
  // When investorId is provided, the investor pays — but the founder can't see
  // the investor's balance, so we skip the check. The backend validates credits on creation.
  const startupBalanceQuery = useStartupCreditBalanceQuery(!investorId ? startup?.id : undefined)
  const creditBalance = investorId ? undefined : startupBalanceQuery.data?.data.balance
  const hasCredits = investorId ? true : creditBalance !== undefined && creditBalance > 0
  const isActivelyRaising = startup?.isFundraising === true

  const [uploadedFile, setUploadedFile] = useState<File | null>(null)
  const [documentId, setDocumentId] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  const createPitchMutation = useCreatePitchMutation()

  const handleUploadComplete = async (docId: string, file: File) => {
    setDocumentId(docId)
    setUploadedFile(file)
  }

  const handleStartPitch = async () => {
    if (!startup?.id || !documentId) return

    try {
      const response = await createPitchMutation.mutateAsync({
        body: {
          startupId: startup.id,
          pitchDeckId: documentId,
          investorId: investorId,
        },
      })

      if (response.status === 'ok') {
        await navigate({
          to: '/pitches/$pitchId',
          params: { pitchId: response.data.id },
        })
      }
    } catch (err) {
      const message = getErrorMessage(err)
      if (investorId && isInsufficientCreditsError(err)) {
        setError('Unable to start this pitch session. Please try again later.')
      } else {
        setError(message)
      }
    }
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  const steps = [
    { icon: Mic, label: 'Live pitch presentation' },
    { icon: MessageSquare, label: 'Interactive Q&A session' },
    { icon: BarChart3, label: 'Detailed scoring & feedback' },
    { icon: Trophy, label: 'Publish to your profile' },
  ]

  return (
    <div className="space-y-6">
      <p className="text-sm text-muted-foreground text-center">
        Pitch live to an AI investor and receive detailed scoring with actionable feedback.
      </p>
      <p className="text-xs text-muted-foreground/70 text-center -mt-4">
        Your startup profile and documents are used during the evaluation — keep them up to date.
      </p>

      {/* How It Works */}
      <div className="grid grid-cols-2 gap-2.5">
        {steps.map((step) => (
          <div
            key={step.label}
            className="flex items-center gap-3 rounded-xl border border-border bg-input/50 px-3.5 py-3"
          >
            <div className="flex items-center justify-center size-8 rounded-lg bg-[#f97316]/10 shrink-0">
              <step.icon className="size-4 text-[#f97316]" />
            </div>
            <p className="text-xs font-medium text-white">{step.label}</p>
          </div>
        ))}
      </div>

      {/* Pitch Deck Upload */}
      {startup && (
        <div className="space-y-2">
          <h2 className="text-sm font-medium text-muted-foreground uppercase tracking-wider">
            Pitch Deck
          </h2>

          {error && <ErrorBanner message={error} />}

          {!documentId ? (
            <DocumentUpload
              onUploadComplete={handleUploadComplete}
              multiple={false}
              accept={{
                'application/pdf': ['.pdf'],
              }}
              labels={{
                singular: 'your pitch deck',
              }}
            />
          ) : (
            <div className="flex items-center gap-3 rounded-xl border border-emerald-500/30 bg-emerald-500/5 px-3.5 py-3">
              <div className="flex items-center justify-center size-8 rounded-lg bg-emerald-500/10 shrink-0">
                <FileText className="size-4 text-emerald-400" />
              </div>
              <span className="text-sm font-medium text-white truncate">{uploadedFile?.name}</span>
              <CheckCircle2 className="size-4.5 text-emerald-400 ml-auto shrink-0" />
            </div>
          )}
        </div>
      )}

      {/* Prerequisite Errors */}
      {startup && !isActivelyRaising && (
        <div className="flex items-center gap-3 rounded-xl border border-red-500/20 bg-red-500/5 px-3.5 py-3">
          <div className="flex items-center justify-center size-8 rounded-lg bg-red-500/10 shrink-0">
            <AlertTriangle className="size-4 text-red-400" />
          </div>
          <p className="text-sm text-red-400">
            Fill in the{' '}
            <Link to="/" className="underline underline-offset-2 hover:text-red-300">
              Actively Raising
            </Link>{' '}
            section on your startup profile to start pitching.
          </p>
        </div>
      )}

      {/* Credit Info */}
      {creditBalance !== undefined &&
        (hasCredits ? (
          <div className="flex items-center gap-3 rounded-xl border border-border bg-input/50 px-3.5 py-3">
            <div className="flex items-center justify-center size-8 rounded-lg bg-elevated shrink-0">
              <Coins className="size-4 text-[#f97316]" />
            </div>
            <p className="text-sm text-muted-foreground">
              <span className="font-medium text-white">1 credit</span> will be used
              <span className="text-xs"> ({creditBalance} remaining)</span>
            </p>
          </div>
        ) : (
          <div className="flex items-center gap-3 rounded-xl border border-red-500/20 bg-red-500/5 px-3.5 py-3">
            <div className="flex items-center justify-center size-8 rounded-lg bg-red-500/10 shrink-0">
              <AlertTriangle className="size-4 text-red-400" />
            </div>
            <p className="text-sm text-red-400">No credits remaining.</p>
          </div>
        ))}

      {/* Action */}
      <Button
        onClick={handleStartPitch}
        disabled={!documentId || createPitchMutation.isPending || !hasCredits || !isActivelyRaising}
        size="lg"
        className="w-full"
      >
        {createPitchMutation.isPending ? 'Creating Session...' : 'Start Pitch Session'}
      </Button>
    </div>
  )
}
